import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../../stores/useStore';
import { Schedule, ScheduleType } from '../../types';
import { FeedbackForm } from '../Feedback/FeedbackForm';
import { EmptyState } from '../Common/EmptyState';

const SCHEDULE_TYPES: ScheduleType[] = ['설치', '수리', '정기점검', '견적', '기타'];

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'white',
    border: '1px solid #c4b8a5',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: "'Noto Sans KR', sans-serif",
    boxSizing: 'border-box',
    color: '#2c2c2c',
};

const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%235a5a5a' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '16px',
    paddingRight: '28px',
};

const fieldLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 700,
    color: '#5a5a5a',
    marginBottom: '4px',
    display: 'block',
};

interface AddForm {
    date: string;
    type: ScheduleType;
    clientId: string;
    machineId: string;
    location: string;
    address: string;
    assignedTo: string[];
    memo: string;
}

type SortMode = 'daily' | 'weekly' | 'monthly';
type ViewMode = 'list' | 'feedback' | 'detail';

interface ScheduleGroup {
    key: string;
    label: string;
    schedules: Schedule[];
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const getWeekStart = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const day = date.getDay();
    date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const formatDateLabel = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const todayStr = new Date().toLocaleDateString('en-CA');
    const tmrw = new Date(); tmrw.setDate(tmrw.getDate() + 1);
    const tmrwStr = tmrw.toLocaleDateString('en-CA');
    let label = `${m}월 ${d}일 (${WEEKDAYS[date.getDay()]})`;
    if (dateStr === todayStr) label += ' — 오늘';
    else if (dateStr === tmrwStr) label += ' — 내일';
    return label;
};

const formatWeekLabel = (weekStart: string): string => {
    const [y, m, d] = weekStart.split('-').map(Number);
    const end = new Date(y, m - 1, d + 6);
    return `${y}년 ${m}월 ${d}일 ~ ${end.getMonth() + 1}월 ${end.getDate()}일`;
};

const formatMonthLabel = (key: string): string => {
    const [y, m] = key.split('-');
    return `${y}년 ${parseInt(m)}월`;
};

const groupSchedules = (schedules: Schedule[], mode: SortMode): ScheduleGroup[] => {
    const map = new Map<string, Schedule[]>();
    schedules.forEach((s) => {
        const key =
            mode === 'daily' ? s.date :
            mode === 'weekly' ? getWeekStart(s.date) :
            s.date.substring(0, 7);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(s);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, items]) => ({
            key,
            label:
                mode === 'daily' ? formatDateLabel(key) :
                mode === 'weekly' ? formatWeekLabel(key) :
                formatMonthLabel(key),
            schedules: items.sort((a, b) =>
                a.date.localeCompare(b.date) || a.timeSlot.start.localeCompare(b.timeSlot.start)
            ),
        }));
};

const STATUS_CONFIG: Record<string, { bg: string; text: string }> = {
    '예정': { bg: '#e8e0d5', text: '#5a5a5a' },
    '완료': { bg: '#5a7a6a', text: 'white' },
    '취소': { bg: '#8b4513', text: 'white' },
};

const TYPE_CONFIG: Record<string, { bg: string; text: string }> = {
    '설치': { bg: '#e3f2fd', text: '#1e3a5f' },
    '수리': { bg: '#ffebee', text: '#8b4513' },
    '정기점검': { bg: '#e8f5e9', text: '#5a7a6a' },
    '견적': { bg: '#f3e5f5', text: '#5a5a5a' },
    '기타': { bg: '#f5f5f5', text: '#5a5a5a' },
};

export const WorkManagement: React.FC = () => {
    const {
        schedules,
        clients,
        machines,
        feedbacks,
        profiles,
        scheduleNotes,
        selectedScheduleId,
        setSelectedSchedule,
        deleteSchedule,
        addSchedule,
        addScheduleNote,
        updateScheduleNote,
        deleteScheduleNote,
    } = useStore();

    const [sortMode, setSortMode] = useState<SortMode>('daily');
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [detailId, setDetailId] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [noteInput, setNoteInput] = useState('');
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingNoteContent, setEditingNoteContent] = useState('');
    const skipDetailEffectRef = useRef(false);

    const todayStr = new Date().toLocaleDateString('en-CA');
    const defaultAddForm = (): AddForm => ({
        date: todayStr,
        type: SCHEDULE_TYPES[0],
        clientId: '',
        machineId: '',
        location: '',
        address: '',
        assignedTo: [],
        memo: '',
    });
    const [addForm, setAddForm] = useState<AddForm>(defaultAddForm());

    const addClientMachines = machines.filter((m) => m.clientId === addForm.clientId);

    const handleAddClientChange = (clientId: string) => {
        const client = clients.find((c) => c.id === clientId);
        setAddForm((f) => ({
            ...f,
            clientId,
            location: client ? client.name : '',
            address: client ? client.contact.address : '',
            machineId: '',
        }));
    };

    const handleAddSubmit = () => {
        if (!addForm.location.trim() || !addForm.memo.trim()) return;
        const newSchedule: Schedule = {
            id: `s_${Date.now()}`,
            date: addForm.date,
            timeSlot: { start: '', end: '' },
            type: addForm.type,
            status: '예정',
            clientId: addForm.clientId,
            ...(addForm.machineId ? { machineId: addForm.machineId } : {}),
            location: addForm.location.trim(),
            address: addForm.address.trim(),
            memo: addForm.memo.trim(),
            assignedTo: addForm.assignedTo,
            createdAt: new Date().toISOString(),
        };
        addSchedule(newSchedule);
        setAddForm(defaultAddForm());
        setShowAddModal(false);
    };

    // 일정 탭에서 "피드백 작성" 눌러 넘어온 경우 자동으로 상세 열기
    useEffect(() => {
        if (selectedScheduleId) {
            if (skipDetailEffectRef.current) {
                skipDetailEffectRef.current = false;
                return;
            }
            setDetailId(selectedScheduleId);
            setViewMode('detail');
            setSelectedSchedule(null);
        }
    }, [selectedScheduleId, setSelectedSchedule]);

    const detailSchedule = detailId ? schedules.find((s) => s.id === detailId) : null;
    const detailClient = detailSchedule?.clientId
        ? clients.find((c) => c.id === detailSchedule.clientId)
        : null;
    const detailMachine = detailSchedule?.machineId
        ? machines.find((m) => m.id === detailSchedule.machineId)
        : null;
    const detailFeedback = detailId
        ? feedbacks.find((f) => f.scheduleId === detailId)
        : null;

    const handleOpenFeedback = (id: string) => {
        skipDetailEffectRef.current = true;
        setSelectedSchedule(id);
        setViewMode('feedback');
    };

    const handleFeedbackComplete = () => {
        setSelectedSchedule(null);
        setViewMode('list');
    };

    const handleDeleteSchedule = (id: string) => {
        if (!window.confirm('이 작업을 삭제하시겠습니까?')) return;
        deleteSchedule(id);
        setDetailId(null);
        setViewMode('list');
    };

    const detailNotes = (detailId ? scheduleNotes.filter((n) => n.scheduleId === detailId) : [])
        .slice().sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    const formatNoteTime = (iso: string): string => {
        const d = new Date(iso);
        return `${d.getMonth() + 1}월 ${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const handleAddNote = () => {
        if (!noteInput.trim() || !detailId) return;
        const now = new Date().toISOString();
        addScheduleNote({ id: `note_${Date.now()}`, scheduleId: detailId, content: noteInput.trim(), createdAt: now, updatedAt: now });
        setNoteInput('');
    };

    const handleSaveNote = () => {
        if (!editingNoteContent.trim() || !editingNoteId) return;
        updateScheduleNote(editingNoteId, editingNoteContent.trim());
        setEditingNoteId(null);
        setEditingNoteContent('');
    };

    if (viewMode === 'feedback') {
        return (
            <div>
                <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#f4f1ea',
                    borderBottom: '1px solid #c4b8a5',
                }}>
                    <button
                        onClick={() => { setSelectedSchedule(null); setViewMode('list'); }}
                        style={{
                            fontSize: '13px',
                            color: '#1e3a5f',
                            fontWeight: 700,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        ← 작업 목록으로
                    </button>
                </div>
                <FeedbackForm onComplete={handleFeedbackComplete} />
            </div>
        );
    }

    if (viewMode === 'detail' && detailSchedule) {
        const typeColor = TYPE_CONFIG[detailSchedule.type] || TYPE_CONFIG['기타'];
        const statusStyle = STATUS_CONFIG[detailSchedule.status] || { bg: '#e8e0d5', text: '#5a5a5a' };
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* 헤더 */}
                <div style={{ backgroundColor: '#1e3a5f', color: 'white', padding: '14px 16px', flexShrink: 0 }}>
                    <button
                        onClick={() => { setDetailId(null); setViewMode('list'); setNoteInput(''); setEditingNoteId(null); }}
                        style={{ background: 'none', border: 'none', color: '#a0b4c8', fontSize: '13px', fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: '10px' }}
                    >
                        ← 목록으로
                    </button>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: typeColor.bg, color: typeColor.text, fontWeight: 700, borderRadius: '10px' }}>
                            {detailSchedule.type}
                        </span>
                        {detailSchedule.status !== '예정' && (
                            <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: statusStyle.bg, color: statusStyle.text, fontWeight: 700, borderRadius: '10px' }}>
                                {detailSchedule.status}
                            </span>
                        )}
                    </div>
                    <h2 style={{ fontSize: '16px', fontWeight: 700 }}>{detailSchedule.memo}</h2>
                </div>

                {/* 스크롤 영역 */}
                <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#d8d0c5', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* 기본 정보 카드 */}
                    <div style={{ backgroundColor: '#f4f1ea', border: '1px solid #c4b8a5', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '10px', fontSize: '14px' }}>
                            <span style={{ color: '#a09890', minWidth: '48px', flexShrink: 0 }}>날짜</span>
                            <span style={{ fontFamily: "'Roboto Mono', monospace", color: '#2c2c2c' }}>{detailSchedule.date}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', fontSize: '14px' }}>
                            <span style={{ color: '#a09890', minWidth: '48px', flexShrink: 0 }}>현장</span>
                            <span style={{ color: '#2c2c2c' }}>{detailSchedule.location}</span>
                        </div>
                        {detailSchedule.address && (
                            <div style={{ display: 'flex', gap: '10px', fontSize: '14px' }}>
                                <span style={{ color: '#a09890', minWidth: '48px', flexShrink: 0 }}>주소</span>
                                <span style={{ color: '#5a5a5a', fontSize: '13px' }}>{detailSchedule.address}</span>
                            </div>
                        )}
                        {detailClient && (
                            <div style={{ display: 'flex', gap: '10px', fontSize: '14px' }}>
                                <span style={{ color: '#a09890', minWidth: '48px', flexShrink: 0 }}>거래처</span>
                                <span style={{ color: '#2c2c2c' }}>{detailClient.name}</span>
                            </div>
                        )}
                        {detailMachine && (
                            <div style={{ display: 'flex', gap: '10px', fontSize: '14px' }}>
                                <span style={{ color: '#a09890', minWidth: '48px', flexShrink: 0 }}>기계</span>
                                <span style={{ color: '#2c2c2c' }}>{detailMachine.name}</span>
                            </div>
                        )}
                        {detailSchedule.assignedTo.length > 0 && (
                            <div style={{ display: 'flex', gap: '10px', fontSize: '14px' }}>
                                <span style={{ color: '#a09890', minWidth: '48px', flexShrink: 0 }}>작업자</span>
                                <span style={{ color: '#2c2c2c' }}>{detailSchedule.assignedTo.join(', ')}</span>
                            </div>
                        )}

                        {/* 피드백 요약 */}
                        {detailFeedback && (
                            <div style={{ marginTop: '4px', paddingTop: '10px', borderTop: '1px solid #e8e0d5', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>
                                <p style={{ fontWeight: 700, color: '#5a7a6a' }}>✓ 피드백 완료</p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <span style={{ color: '#a09890' }}>상태</span>
                                    <span style={{ padding: '1px 6px', backgroundColor: detailFeedback.condition === '양호' ? '#e8f5e9' : '#ffebee', color: detailFeedback.condition === '양호' ? '#5a7a6a' : '#c75c2e', borderRadius: '10px', fontWeight: 700 }}>
                                        {detailFeedback.condition}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <span style={{ color: '#a09890' }}>정산</span>
                                    <span style={{ fontFamily: "'Roboto Mono', monospace" }}>{detailFeedback.totalCost.toLocaleString()}원 ({detailFeedback.paymentMethod})</span>
                                </div>
                            </div>
                        )}

                        {/* 액션 버튼 */}
                        <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {detailSchedule.status !== '완료' && detailSchedule.status !== '취소' && (
                                <button
                                    onClick={() => handleOpenFeedback(detailSchedule.id)}
                                    style={{ width: '100%', padding: '12px', backgroundColor: '#1e3a5f', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
                                >
                                    작업 완료 및 피드백 작성
                                </button>
                            )}
                            <button
                                onClick={() => handleDeleteSchedule(detailSchedule.id)}
                                style={{ width: '100%', padding: '11px', backgroundColor: 'white', color: '#c75c2e', border: '1px solid #c75c2e', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                            >
                                작업 삭제
                            </button>
                        </div>
                    </div>

                    {/* 메모 섹션 */}
                    <div>
                        <p style={{ fontSize: '12px', fontWeight: 700, color: '#5a5a5a', marginBottom: '8px', letterSpacing: '0.5px' }}>
                            메모 ({detailNotes.length})
                        </p>

                        {/* 메모 입력 */}
                        <div style={{ backgroundColor: '#f4f1ea', border: '1px solid #c4b8a5', borderRadius: '12px', padding: '12px', marginBottom: '8px' }}>
                            <textarea
                                value={noteInput}
                                onChange={(e) => setNoteInput(e.target.value)}
                                placeholder="필요한 자재, 작업 내용 등을 입력하세요"
                                style={{
                                    width: '100%', height: '72px', padding: '10px 12px',
                                    backgroundColor: 'white', border: '1px solid #c4b8a5', borderRadius: '10px',
                                    fontSize: '14px', resize: 'none', outline: 'none',
                                    fontFamily: "'Noto Sans KR', sans-serif", boxSizing: 'border-box', color: '#2c2c2c',
                                }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                                <button
                                    onClick={handleAddNote}
                                    disabled={!noteInput.trim()}
                                    style={{
                                        padding: '8px 20px',
                                        backgroundColor: noteInput.trim() ? '#c75c2e' : '#c4b8a5',
                                        color: 'white', border: 'none', borderRadius: '10px',
                                        fontSize: '13px', fontWeight: 700,
                                        cursor: noteInput.trim() ? 'pointer' : 'not-allowed',
                                    }}
                                >
                                    등록
                                </button>
                            </div>
                        </div>

                        {/* 메모 목록 */}
                        {detailNotes.length === 0 ? (
                            <div style={{ backgroundColor: '#f4f1ea', border: '1px solid #c4b8a5', borderRadius: '12px', padding: '20px', textAlign: 'center', color: '#a09890', fontSize: '13px' }}>
                                등록된 메모가 없습니다
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {detailNotes.map((note) => (
                                    <div key={note.id} style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', borderRadius: '12px', padding: '12px' }}>
                                        {editingNoteId === note.id ? (
                                            <>
                                                <textarea
                                                    value={editingNoteContent}
                                                    onChange={(e) => setEditingNoteContent(e.target.value)}
                                                    style={{
                                                        width: '100%', height: '72px', padding: '10px 12px',
                                                        backgroundColor: '#f4f1ea', border: '1px solid #c75c2e', borderRadius: '10px',
                                                        fontSize: '14px', resize: 'none', outline: 'none',
                                                        fontFamily: "'Noto Sans KR', sans-serif", boxSizing: 'border-box', color: '#2c2c2c',
                                                    }}
                                                />
                                                <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                                                    <button
                                                        onClick={handleSaveNote}
                                                        style={{ flex: 1, padding: '8px', backgroundColor: '#1e3a5f', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                                                    >
                                                        저장
                                                    </button>
                                                    <button
                                                        onClick={() => { setEditingNoteId(null); setEditingNoteContent(''); }}
                                                        style={{ flex: 1, padding: '8px', backgroundColor: 'white', color: '#5a5a5a', border: '1px solid #c4b8a5', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                                                    >
                                                        취소
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p style={{ fontSize: '14px', color: '#2c2c2c', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{note.content}</p>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                                                    <span style={{ fontSize: '11px', color: '#a09890', fontFamily: "'Roboto Mono', monospace" }}>
                                                        {formatNoteTime(note.createdAt)}
                                                        {note.updatedAt !== note.createdAt && ' (수정됨)'}
                                                    </span>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button
                                                            onClick={() => { setEditingNoteId(note.id); setEditingNoteContent(note.content); }}
                                                            style={{ padding: '4px 10px', backgroundColor: 'white', border: '1px solid #c4b8a5', borderRadius: '10px', fontSize: '12px', color: '#5a5a5a', cursor: 'pointer', fontWeight: 700 }}
                                                        >
                                                            수정
                                                        </button>
                                                        <button
                                                            onClick={() => { if (window.confirm('메모를 삭제하시겠습니까?')) deleteScheduleNote(note.id); }}
                                                            style={{ padding: '4px 10px', backgroundColor: 'white', border: '1px solid #c75c2e', borderRadius: '10px', fontSize: '12px', color: '#c75c2e', cursor: 'pointer', fontWeight: 700 }}
                                                        >
                                                            삭제
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const allGroups = groupSchedules(schedules, sortMode);

    const today = new Date().toLocaleDateString('en-CA');
    const thisMonthKey = today.substring(0, 7);
    const getThisMonday = (): string => {
        const d = new Date();
        const diff = d.getDay() === 0 ? 6 : d.getDay() - 1;
        d.setDate(d.getDate() - diff);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const groups = (() => {
        if (sortMode === 'daily') {
            const upcoming = allGroups.filter((g) => g.key >= today);
            return upcoming.length > 0 ? [upcoming[0]] : allGroups.length > 0 ? [allGroups[allGroups.length - 1]] : [];
        }
        if (sortMode === 'weekly') return allGroups.filter((g) => g.key === getThisMonday());
        return allGroups.filter((g) => g.key === thisMonthKey);
    })();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 정렬 탭 */}
            <div style={{
                display: 'flex',
                backgroundColor: '#f4f1ea',
                borderBottom: '2px solid #c4b8a5',
                flexShrink: 0,
            }}>
                {([['daily', '일간'], ['weekly', '주간'], ['monthly', '월간']] as [SortMode, string][]).map(([mode, label]) => (
                    <button
                        key={mode}
                        onClick={() => setSortMode(mode)}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            fontSize: '13px',
                            fontWeight: sortMode === mode ? 700 : 400,
                            color: sortMode === mode ? '#1e3a5f' : '#5a5a5a',
                            borderBottom: sortMode === mode ? '3px solid #c75c2e' : '3px solid transparent',
                            cursor: 'pointer',
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* 작업 목록 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {groups.length === 0 ? (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#a09890', letterSpacing: '0.5px' }}>
                                작업 없음
                            </span>
                            <button
                                onClick={() => { setAddForm(defaultAddForm()); setShowAddModal(true); }}
                                style={{ padding: '6px 12px', backgroundColor: '#1e3a5f', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                            >
                                + 추가
                            </button>
                        </div>
                        <EmptyState
                            message={schedules.length === 0 ? '등록된 작업이 없습니다' : '이 기간에 작업이 없습니다'}
                            subMessage={schedules.length === 0 ? '추가 버튼으로 등록해보세요' : ''}
                        />
                    </>
                ) : (
                    groups.map((group) => (
                        <div key={group.key}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                                paddingBottom: '4px',
                                borderBottom: '1px solid #e8e0d5',
                            }}>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#a09890', letterSpacing: '0.5px' }}>
                                    {group.label}
                                </span>
                                <button
                                    onClick={() => { setAddForm(defaultAddForm()); setShowAddModal(true); }}
                                    style={{ padding: '6px 12px', backgroundColor: '#1e3a5f', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                                >
                                    + 추가
                                </button>
                            </div>
                            <div style={{ border: '1px solid #c4b8a5', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden' }}>
                                {group.schedules.map((schedule, idx) => {
                                    const isLast = idx === group.schedules.length - 1;
                                    const typeColor = TYPE_CONFIG[schedule.type] || TYPE_CONFIG['기타'];
                                    const client = clients.find((c) => c.id === schedule.clientId);
                                    return (
                                        <div
                                            key={schedule.id}
                                            onClick={() => { setDetailId(schedule.id); setViewMode('detail'); setNoteInput(''); setEditingNoteId(null); }}
                                            style={{
                                                padding: '12px 16px',
                                                borderBottom: isLast ? 'none' : '1px solid #e8e0d5',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#faf8f5'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                                                <span style={{
                                                    fontSize: '10px',
                                                    padding: '2px 7px',
                                                    backgroundColor: typeColor.bg,
                                                    color: typeColor.text,
                                                    fontWeight: 700,
                                                    borderRadius: '10px',
                                                }}>
                                                    {schedule.type}
                                                </span>
                                                {sortMode !== 'daily' && (() => {
                                                    const [, sm, sd] = schedule.date.split('-').map(Number);
                                                    return (
                                                        <span style={{
                                                            fontSize: '10px',
                                                            padding: '2px 7px',
                                                            backgroundColor: '#f4f1ea',
                                                            color: '#5a5a5a',
                                                            fontWeight: 700,
                                                            borderRadius: '10px',
                                                            border: '1px solid #e8e0d5',
                                                        }}>
                                                            {sm}월{sd}일
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <p style={{ fontWeight: 700, color: '#2c2c2c', fontSize: '14px', marginBottom: '3px' }}>
                                                {schedule.memo}
                                            </p>
                                            <p style={{ fontSize: '12px', color: '#666' }}>
                                                {client ? client.name : schedule.location}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* 작업 추가 모달 */}
            {showAddModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                    onClick={() => setShowAddModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#f4f1ea',
                            borderTop: '2px solid #c75c2e',
                            borderRadius: '20px 20px 0 0',
                            width: '100%',
                            maxHeight: '88vh',
                            overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            backgroundColor: '#1e3a5f',
                            color: 'white',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <span style={{ fontWeight: 700, fontSize: '15px' }}>작업 추가</span>
                            <button
                                onClick={() => setShowAddModal(false)}
                                style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={fieldLabelStyle}>날짜 *</label>
                                <input
                                    type="date"
                                    value={addForm.date}
                                    onChange={(e) => setAddForm((f) => ({ ...f, date: e.target.value }))}
                                    style={inputStyle}
                                />
                            </div>

                            <div>
                                <label style={fieldLabelStyle}>작업 유형</label>
                                <select
                                    value={addForm.type}
                                    onChange={(e) => setAddForm((f) => ({ ...f, type: e.target.value as ScheduleType }))}
                                    style={selectStyle}
                                >
                                    {SCHEDULE_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            {clients.length > 0 && (
                                <div>
                                    <label style={fieldLabelStyle}>거래처 선택 (선택사항)</label>
                                    <select
                                        value={addForm.clientId}
                                        onChange={(e) => handleAddClientChange(e.target.value)}
                                        style={selectStyle}
                                    >
                                        <option value="">— 직접 입력 —</option>
                                        {clients.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label style={fieldLabelStyle}>현장명 *</label>
                                <input
                                    value={addForm.location}
                                    onChange={(e) => setAddForm((f) => ({ ...f, location: e.target.value }))}
                                    placeholder="예: 강남 빌딩"
                                    style={inputStyle}
                                />
                            </div>

                            <div>
                                <label style={fieldLabelStyle}>주소</label>
                                <input
                                    value={addForm.address}
                                    onChange={(e) => setAddForm((f) => ({ ...f, address: e.target.value }))}
                                    placeholder="예: 서울특별시 강남구 ..."
                                    style={inputStyle}
                                />
                            </div>

                            {addForm.clientId && addClientMachines.length > 0 && (
                                <div>
                                    <label style={fieldLabelStyle}>기계 선택 (선택사항)</label>
                                    <select
                                        value={addForm.machineId}
                                        onChange={(e) => setAddForm((f) => ({ ...f, machineId: e.target.value }))}
                                        style={selectStyle}
                                    >
                                        <option value="">— 없음 —</option>
                                        {addClientMachines.map((m) => (
                                            <option key={m.id} value={m.id}>{m.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {profiles.length > 0 && (
                                <div>
                                    <label style={fieldLabelStyle}>작업자</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {profiles.map((p) => {
                                            const active = addForm.assignedTo.includes(p.name);
                                            return (
                                                <button
                                                    key={p.id}
                                                    type="button"
                                                    onClick={() => setAddForm((f) => ({
                                                        ...f,
                                                        assignedTo: active
                                                            ? f.assignedTo.filter((n) => n !== p.name)
                                                            : [...f.assignedTo, p.name],
                                                    }))}
                                                    style={{
                                                        padding: '8px 14px',
                                                        border: active ? '2px solid #1e3a5f' : '1px solid #c4b8a5',
                                                        borderRadius: '20px',
                                                        backgroundColor: active ? '#1e3a5f' : 'white',
                                                        color: active ? 'white' : '#5a5a5a',
                                                        fontSize: '13px',
                                                        fontWeight: active ? 700 : 400,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {p.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label style={fieldLabelStyle}>작업 내용 *</label>
                                <textarea
                                    value={addForm.memo}
                                    onChange={(e) => setAddForm((f) => ({ ...f, memo: e.target.value }))}
                                    placeholder="작업 내용을 입력하세요"
                                    style={{ ...inputStyle, height: '80px', resize: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '8px', paddingTop: '4px', paddingBottom: '8px' }}>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    style={{
                                        flex: 1, padding: '13px', border: '1px solid #c4b8a5',
                                        backgroundColor: 'white', fontSize: '14px', fontWeight: 700,
                                        cursor: 'pointer', borderRadius: '10px', color: '#5a5a5a',
                                    }}
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleAddSubmit}
                                    disabled={!addForm.location.trim() || !addForm.memo.trim()}
                                    style={{
                                        flex: 2, padding: '13px', border: 'none',
                                        backgroundColor: (addForm.location.trim() && addForm.memo.trim()) ? '#c75c2e' : '#c4b8a5',
                                        color: 'white', fontSize: '14px', fontWeight: 700,
                                        cursor: (addForm.location.trim() && addForm.memo.trim()) ? 'pointer' : 'not-allowed',
                                        borderRadius: '10px',
                                    }}
                                >
                                    작업 추가
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
